import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaPhoneAlt, FaVoicemail, FaHeadset, FaCheckCircle, FaArrowRight, FaPhone, FaMobileAlt, FaCloud, FaCogs } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const TelephonyPage = () => {
    const services = [
        {
            icon: <FaPhoneAlt size={24} />,
            title: 'VoIP Phone Systems',
            description: 'Modern Voice over IP solutions with crystal-clear audio and advanced features for business communication.',
            gradient: 'from-pink-600 to-rose-600'
        },
        {
            icon: <FaCloud size={24} />,
            title: 'Cloud PBX',
            description: 'Fully managed cloud-based phone systems with no hardware maintenance required.',
            gradient: 'from-purple-600 to-violet-600'
        },
        {
            icon: <FaHeadset size={24} />,
            title: 'Unified Communications',
            description: 'Integrated voice, video, messaging, and collaboration tools in one platform.',
            gradient: 'from-blue-600 to-cyan-600'
        },
        {
            icon: <FaVoicemail size={24} />,
            title: 'Voicemail & Auto Attendant',
            description: 'Professional voicemail systems with visual voicemail and auto-attendant features.',
            gradient: 'from-teal-500 to-green-500'
        },
        {
            icon: <FaMobileAlt size={24} />,
            title: 'Mobile Integration',
            description: 'Seamless mobile apps that extend your business phone system to any device.',
            gradient: 'from-orange-500 to-amber-500'
        },
        {
            icon: <FaCogs size={24} />,
            title: 'System Maintenance',
            description: 'Ongoing support and maintenance to keep your phone systems running optimally.',
            gradient: 'from-indigo-600 to-blue-600'
        }
    ];

    const benefits = [
        'Reduced phone bills by 50%+',
        'Work from anywhere capability',
        'Enterprise-grade features',
        'Easy scalability',
        'Professional call handling',
        '24/7 support available'
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-pink-900 to-rose-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-pink-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-rose-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-pink-400">Telephony Systems</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                Modern <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-400">Telephony</span> Systems
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Upgrade to next-generation phone systems with VoIP and unified communications for seamless business connectivity.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-600 to-rose-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-pink-500/30 transition-all duration-300">
                                    Get Started
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-pink-600/20 to-rose-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=600&h=400&fit=crop"
                                    alt="Telephony Systems"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-pink-100 text-pink-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Complete Telephony Solutions</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            From traditional PBX to modern cloud systems, we provide end-to-end telephony services
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-pink-200"
                            >
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-pink-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&h=500&fit=crop"
                                alt="Business Call"
                                className="w-full h-auto rounded-2xl shadow-2xl"
                            />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-pink-100 text-pink-700 rounded-full text-sm font-semibold mb-4">Why Choose Us</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">The Future of Business Communications</h2>
                            <p className="text-lg text-gray-600 mb-8">
                                Our telephony solutions empower your team to communicate effectively from anywhere while reducing costs.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <FaCheckCircle className="text-pink-600 flex-shrink-0" size={20} />
                                        <span className="text-gray-700 font-medium">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-pink-600 to-rose-600">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Modernize Your Phone System?</h2>
                    <p className="text-xl text-pink-100 mb-8">
                        Get a free assessment and see how much you can save with our telephony solutions.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-pink-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                            Get Free Quote
                            <FaArrowRight size={14} />
                        </Link>
                        <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                            <FaPhone size={14} />
                            Call Now
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default TelephonyPage;
